import React, { useState, useMemo } from 'react'
import Scrollspy from 'react-scrollspy'
import Layout from '../components/Layout'
import Header from '../components/Header'
import { graphql, PageProps } from 'gatsby'
import { IndexQuery } from '../../types/graphql-types'
import Section from '../components/Section'
import Contact from '../components/Contact'
import outdent from 'outdent'
import { nl2Br } from '../utils/nl2Br'
import Profile from '../components/Profile'
import { FluidObject } from 'gatsby-image'
import { randomize } from '../utils/randomize'
import { message, members } from '../utils/data'

const IndexPage: React.FC<PageProps<IndexQuery>> = ({ data }) => {
  const [natsukashimu] = useState(
    randomize(
      data.natsukashimu1,
      data.natsukashimu2,
      data.natsukashimu3,
      data.natsukashimu4,
      data.natsukashimu5,
      data.natsukashimu6,
      data.natsukashimu7
    )
  )
  const [yurutabi] = useState(randomize(data.yurutabi1, data.yurutabi2))

  const songs: Song[] = useMemo(
    () => [
      {
        image: data.kimiumi!.childImageSharp!.fluid!,
        title: '君を海に誘ったんだ',
        link: 'https://www.youtube.com/watch?v=0ETk90IBYmk',
        content: outdent`
          Shiny!　少し勇気を出して
          君を海に誘ったんだ
          今　湧き上がる気持ちが
          ふつふつと夏を告げるよ
          Let's Go!　返事もらえたとき
          スマホ持つ手震えたんだ
          遅くなったけど　待ちわびた季節
          はじまるね…
        `,
      },
      {
        image: data.phoenix!.childImageSharp!.fluid!,
        title: '不死鳥のパズル',
        link: 'https://www.youtube.com/watch?v=En2LKdYqCSs',
        content: outdent`
          俄然と現れた　通りすがり
          奇跡の日常　だけど
          いつか冷えてく　偽り
          
          葛藤な日々　遠ざかるたび
          身体の真芯が　閉じて
          全部塞がっていく
          
          そして
          ただ過ぎる・こなしてく
          立ち止まる・ときは経つ
          ５つ数えて　不死鳥になる
        `,
      },
      {
        image: data.summerBreeze!.childImageSharp!.fluid!,
        title: '気まぐれSummer Breeze',
        link: 'https://www.youtube.com/watch?v=E2WXb6WGSBw',
        content: outdent`
          好きって　その気色を
          その兆しを　確かめたら
          ブレーキなんて僕には要らないよ
          
          どこかに響いてる
          火花の音　爆ぜたような
          イメージできたら　幕開けだ
        `,
      },
      {
        image: data.namida!.childImageSharp!.fluid!,
        title: '涙も出ぬ夜',
        link: 'https://www.youtube.com/watch?v=xq4uiFNRc8M',
        content: outdent`
          さあ　希望だけが
          私を待っている
          めくるめく不安など
          在りはしないわ
          
          現実から目を
          そらした私を
          慰められるなら
          そばにいてほしい
        `,
      },
      {
        image: data.promise!.childImageSharp!.fluid!,
        title: '約束',
        link: 'https://www.youtube.com/watch?v=5w8C7TeqZTo',
        content: outdent`
          これからも一緒でいようなんて
          約束を交わしたとしても
          戻り得ない時に
          ずっと浸っていたい
          離れることが怖くて泣いた
        `,
      },
      {
        image: data.valentine!.childImageSharp!.fluid!,
        mobile: data.valentineMobile!.childImageSharp!.fluid!,
        title: 'マイ・ハッピー・バレンタイン',
        link: 'https://www.youtube.com/watch?v=m5Bcm30gnXM',
        content: outdent`
          さあ　始まる
          小さな夢の物語
          ほら　届けたい
          私の願いを
        `,
      },
      {
        image: data.sakuraPocket!.childImageSharp!.fluid!,
        mobile: data.sakuraPocketMobile!.childImageSharp!.fluid!,
        title: '桜ポケット',
        link: 'https://www.youtube.com/watch?v=SICivod3SGI',
        content: outdent`
          固く閉ざした　胸のポケットの中
          開けないままで　君は笑顔のままで
          保っていた　心の粒が溢れないように
        `,
      },
      {
        image: data.tampopoNoUta!.childImageSharp!.fluid!,
        title: 'たんぽぽの唄',
        link: 'https://www.youtube.com/watch?v=WQYcmdt3iSA',
        content: outdent`
          窓を開けたら　吹き込んだ風に身をまかせ
          青空の下　奏でる音に
          乗せた想いよ　君に届け
          ふと見下ろせば　揺れる同じ色のたんぽぽ
          遠く走る君の影と
          夕陽を目で追いかけてたんだ
        `,
      },
      {
        image: data.fantastic!.childImageSharp!.fluid!,
        title: 'ファンタスティック・パレード',
        link: 'https://www.youtube.com/watch?v=TQVKP7m8Hlc',
        content: outdent`
          金色の地球　踊り出すミラーボール
          聖なる夜の魔法　言葉にして
          秘めた願い事だって
          叶っちゃうかもよ　そんな
          ファンタスティック・
          スター・ナイト・パレード
        `,
      },
      {
        image: yurutabi!.childImageSharp!.fluid!,
        title: 'ゆるたびきっぷ',
        link: 'https://www.youtube.com/watch?v=RNe9Tq_c9B0',
        content: outdent`
          普通列車　車窓の中
          雪景色に包まれたら
          ささやかな　ひとり旅の
          始まりはじまり
        `,
      },
      {
        image: data.coffeeTime!.childImageSharp!.fluid!,
        title: 'コーヒータイムはエンドレス',
        link: 'https://www.youtube.com/watch?v=JmWG857QrC0',
        content: outdent`
          迎えたのだチャンスを今
          言い聞かせてる
          二度と来ないって
          リカバーすればするほど
          天地返ったような
          錯覚に襲われてる
          コーヒーはどんどん冷めてく
        `,
      },
      {
        image: data.tsuredure!.childImageSharp!.fluid!,
        title: 'つれづれ日記',
        link: 'https://www.youtube.com/watch?v=klq5VfYtdGo',
        content: outdent`
          「一番伝えたいこと」とか、
          別にあるわけじゃないけど、
          この気分を分ち合えたなら、
          私ひとりきりじゃないって、
          そう思えるから今日の日も、
          出会ってくれてありがとう。
        `,
      },
      {
        image: data.ketsui!.childImageSharp!.fluid!,
        title: '決意のキャラメル',
        link: 'https://www.youtube.com/watch?v=obBMmx81qCE',
        content: outdent`
          尊めの秋と、青空に惹かれ
          香ばしい空気と、静けさが　追い風になる
          自信はないけれど　踏ん張っていられるから
          決戦　走っていく時間を　追い越して
          見える景色さえ　独り占めしたいよ
          きっとできるって
        `,
      },
      {
        image: natsukashimu!.childImageSharp!.fluid!,
        title: '懐かしむコト',
        link: 'https://www.youtube.com/watch?v=02fRDMjyVeE',
        content: outdent`
          画面をいじれば　君がいるのも
          頭では判ってるのに
          約束もせずに笑ってたあの頃
          心の中　贅沢ねって　笑った
        `,
      },
      {
        image: data.bokuraNoYume!.childImageSharp!.fluid!,
        title: '僕らの夢',
        link: 'https://www.youtube.com/watch?v=oQI7tLCF9DY',
        content: outdent`
          月に祈った　どんな思いも
          胸に刻んだ　誓いも
          今日の夢には　叶わないよ
          嘘は言わなくていい
          そう言って君の手を強く握ったんだ
        `,
      },
      {
        image: data.kazeKaoru!.childImageSharp!.fluid!,
        title: '風薫る物語',
        link: 'https://www.youtube.com/watch?v=oQlZqHiSoSc',
        content: outdent`
          だんだん近づいてく
          まあるい心の距離
          あと少し　あと少し
          触れられる気がした
          でも気が付けばいつも
          話しかけてんのは
          夢ん中の私だった
        `,
      },
      {
        image: data.judenKanryo!.childImageSharp!.fluid!,
        title: '充電完了！',
        link: 'https://www.youtube.com/watch?v=Ut4sPe_yqFg',
        content: outdent`
          当たり前ばかり　重ねたら
          いつの間にか　消える
          熱情に満ちた　その輝き
          まちに見られてる　聴かれてる
          そう思えたらさ
          シャキッとしてられるから
          約束だよ
        `,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <IndexPagePresenter
      topMessage={message}
      members={members}
      songs={songs}
      overlay={data.overlay!.childImageSharp!.fluid!}
    />
  )
}

type Song = {
  image: FluidObject
  mobile?: FluidObject
  title: string
  link?: string
  content: string
}
type Props = {
  topMessage: string
  members: { name: string; part: string }[]
  songs: Song[]
  overlay: FluidObject
}
const IndexPagePresenter: React.FC<Props> = ({
  topMessage,
  members,
  songs,
  overlay,
}) => (
  <Layout
    // トップ画像は適宜調整
    desktopImage={[overlay, songs[0].image]}
    mobileImage={[overlay, songs[0].mobile ?? songs[0].image]}
  >
    <Header message={topMessage} />

    <Profile members={members} next={encodeURIComponent(songs[0].title)} />

    {songs.map((props, i, arr) => (
      <Section
        {...props}
        key={props.title}
        next={arr[i + 1]?.title ?? 'footer'}
        isFirst={i === 0}
      >
        <p>{nl2Br(props.content)}</p>
      </Section>
    ))}

    <Contact />

    <Scrollspy
      items={['top', ...songs.map((s) => encodeURIComponent(s.title))]}
      currentClassName="active"
      offset={-150}
      componentTag="div"
    >
      <div />
      {songs.map((s) => {
        return (
          <div
            key={s.title}
            className="main-bg"
            id={`${encodeURIComponent(s.title)}-bg`}
            style={{
              backgroundImage: `url(${overlay.src}), url(${s.image.src})`,
            }}
          />
        )
      })}
    </Scrollspy>
  </Layout>
)

export const query = graphql`
  query Index {
    overlay: file(relativePath: { eq: "overlay.png" }) {
      childImageSharp {
        fluid(maxWidth: 400) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    tampopoNoUta: file(relativePath: { eq: "tampopo_no_uta.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    sakuraPocket: file(relativePath: { eq: "sakura_pocket.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    sakuraPocketMobile: file(relativePath: { eq: "sakura_pocket_mobile.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    kazeKaoru: file(relativePath: { eq: "kaze_kaoru.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    bokuraNoYume: file(relativePath: { eq: "bokura_no_yume.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    judenKanryo: file(relativePath: { eq: "juden_kanryo.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    natsukashimu1: file(relativePath: { eq: "natsukashimu/オレンジ.JPG" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    natsukashimu2: file(relativePath: { eq: "natsukashimu/ピンク.JPG" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    natsukashimu3: file(relativePath: { eq: "natsukashimu/黄色.JPG" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    natsukashimu4: file(relativePath: { eq: "natsukashimu/黄緑.JPG" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    natsukashimu5: file(relativePath: { eq: "natsukashimu/水色.JPG" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    natsukashimu6: file(relativePath: { eq: "natsukashimu/赤.JPG" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    natsukashimu7: file(relativePath: { eq: "natsukashimu/緑.JPG" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    ketsui: file(relativePath: { eq: "ketsui_no_caramel.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    tsuredure: file(relativePath: { eq: "tsuredure_diary.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    coffeeTime: file(relativePath: { eq: "coffeetime_endless.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    yurutabi1: file(relativePath: { eq: "yurutabi/1.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    yurutabi2: file(relativePath: { eq: "yurutabi/2.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    fantastic: file(relativePath: { eq: "fantastic_parade.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    valentine: file(relativePath: { eq: "my_happy_valentine.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    valentineMobile: file(
      relativePath: { eq: "my_happy_valentine_mobile.png" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    promise: file(relativePath: { eq: "promise.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    namida: file(relativePath: { eq: "namida.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    summerBreeze: file(relativePath: { eq: "summer_breeze.jpeg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    phoenix: file(relativePath: { eq: "puzzle_of_phoenix.jpeg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    kimiumi: file(relativePath: { eq: "kimiumi.jpeg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`

export default IndexPage
